<template>
  <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
    <div class="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div class="flex items-center justify-between mb-5">
        <div>
          <h3 class="m-0 text-lg font-bold text-heading">Hero section</h3>
          <p class="text-sm text-slate-500 m-0 mt-1">Only filled fields appear on the public page</p>
        </div>
      </div>

      <div class="mb-6">
        <p class="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Layout</p>
        <div class="grid grid-cols-2 gap-3">
          <button
            type="button"
            class="px-4 py-3 text-sm font-semibold border rounded-lg cursor-pointer transition-colors duration-200"
            :class="form.layout === 'composed' ? 'bg-primary text-white border-primary' : 'bg-white text-heading border-gray-300'"
            @click="form.layout = 'composed'"
          >
            Composed UI
          </button>
          <button
            type="button"
            class="px-4 py-3 text-sm font-semibold border rounded-lg cursor-pointer transition-colors duration-200"
            :class="form.layout === 'banner' ? 'bg-primary text-white border-primary' : 'bg-white text-heading border-gray-300'"
            @click="form.layout = 'banner'"
          >
            Banner image
          </button>
        </div>
      </div>

      <div v-if="form.layout === 'banner'" class="mb-6">
        <p class="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Banner image</p>
        <button
          type="button"
          class="w-full border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-gray-50 cursor-pointer hover:border-six"
          @click="openPicker('banner')"
        >
          <div v-if="bannerUrl" class="h-40 bg-cover bg-center" :style="{ backgroundImage: 'url(' + bannerUrl + ')' }"></div>
          <div v-else class="py-10 text-slate-500 text-sm">Choose from media library</div>
        </button>
        <button
          v-if="form.banner_media_id"
          type="button"
          class="mt-2 text-xs text-red-600 bg-transparent border-0 cursor-pointer"
          @click="clearMedia('banner')"
        >
          Remove banner
        </button>
      </div>

      <div v-if="form.layout === 'composed'" class="space-y-4">
        <div>
          <label class="block text-sm font-semibold text-heading mb-1">Upper small text</label>
          <input v-model="form.kicker" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Institute of Applied Technology" />
        </div>
        <div>
          <label class="block text-sm font-semibold text-heading mb-1">Title</label>
          <input v-model="form.title" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="IAT CAREER FAIR 2025" />
        </div>
        <div>
          <label class="block text-sm font-semibold text-heading mb-1">Tagline</label>
          <input v-model="form.tagline" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Connect. Explore. Succeed." />
        </div>
        <div>
          <label class="block text-sm font-semibold text-heading mb-1">Hashtag</label>
          <input v-model="form.hashtag" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="#IATCareerFair2025" />
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="border border-gray-200 rounded-lg p-3">
            <p class="text-xs font-bold text-slate-500 mb-2">Button 1</p>
            <input v-model="form.button_1_label" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2" placeholder="Label" />
            <input v-model="form.button_1_url" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="URL or #section" />
          </div>
          <div class="border border-gray-200 rounded-lg p-3">
            <p class="text-xs font-bold text-slate-500 mb-2">Button 2</p>
            <input v-model="form.button_2_label" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2" placeholder="Label" />
            <input v-model="form.button_2_url" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="URL or #section" />
          </div>
        </div>

        <div>
          <p class="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Circle portraits (up to 4)</p>
          <div class="grid grid-cols-4 gap-4 pt-2 pr-2">
            <div
              v-for="slot in circleSlots"
              :key="slot.key"
              class="cf-circle-wrap"
            >
              <button
                type="button"
                class="cf-circle-btn"
                :class="circleUrl(slot.key) ? 'cf-circle-btn--filled' : ''"
                @click="openPicker(slot.key)"
              >
                <img
                  v-if="circleUrl(slot.key)"
                  :src="circleUrl(slot.key)"
                  :alt="'Portrait ' + slot.label"
                  class="cf-circle-img"
                />
                <span v-else class="text-xs text-slate-400">{{ slot.label }}</span>
              </button>
              <button
                v-if="form[slot.field]"
                type="button"
                class="cf-circle-close"
                aria-label="Remove portrait"
                @click.stop="clearMedia(slot.key)"
              >
                &times;
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6 flex justify-end">
        <button
          type="button"
          class="px-5 py-2 text-sm font-bold text-white bg-primary border-0 rounded-lg cursor-pointer hover:bg-primary-600"
          :disabled="saving"
          @click="save"
        >
          {{ saving ? "Saving..." : "Save hero" }}
        </button>
      </div>
    </div>

    <HeroLivePreview :hero-data="previewHeroData" />

    <MediaLibraryPicker
      v-if="pickerTarget"
      media-type="image"
      folder="career-fair"
      @close="pickerTarget = null"
      @select="onMediaSelected"
    />
  </div>
</template>

<script>
import gql from "graphql-tag";
import MediaLibraryPicker from "./MediaLibraryPicker.vue";
import HeroLivePreview from "./HeroLivePreview.vue";
import { mediaPublicUrl, CAREER_FAIR_HERO_FIELDS, cloneApolloData } from "../graphql";

const SAVE_MUTATION = gql`
  mutation UpsertCareerFairHero($career_fair_id: ID!, $input: CareerFairHeroInput!) {
    upsertCareerFairHero(career_fair_id: $career_fair_id, input: $input) {
      error
      message
      data {
        ${CAREER_FAIR_HERO_FIELDS}
      }
    }
  }
`;

export default {
  name: "HeroSectionEditor",
  components: { MediaLibraryPicker, HeroLivePreview },
  props: {
    careerFairId: { type: [String, Number], required: true },
    hero: { type: Object, default: null },
  },
  data() {
    return {
      saving: false,
      pickerTarget: null,
      mediaCache: {},
      form: this.emptyForm(),
      circleSlots: [
        { key: "circle1", field: "circle_1_media_id", label: "1" },
        { key: "circle2", field: "circle_2_media_id", label: "2" },
        { key: "circle3", field: "circle_3_media_id", label: "3" },
        { key: "circle4", field: "circle_4_media_id", label: "4" },
      ],
    };
  },
  computed: {
    bannerUrl() {
      return this.urlFor(this.form.banner_media_id);
    },
    circleUrls() {
      var urls = [];
      var i;
      var slot;
      for (i = 0; i < this.circleSlots.length; i++) {
        slot = this.circleSlots[i];
        if (this.circleUrl(slot.key)) urls.push(this.circleUrl(slot.key));
      }
      return urls;
    },
    hasComposedPreview() {
      var f = this.form;
      return !!(f.kicker || f.title || f.tagline || f.hashtag || f.button_1_label || f.button_2_label || this.circleUrls.length);
    },
    previewHeroData() {
      var buttons = [];
      if (this.form.button_1_label) {
        buttons.push({ label: this.form.button_1_label, url: this.form.button_1_url || "" });
      }
      if (this.form.button_2_label) {
        buttons.push({ label: this.form.button_2_label, url: this.form.button_2_url || "" });
      }
      return {
        layout: this.form.layout,
        bannerUrl: this.bannerUrl,
        kicker: this.form.kicker || "",
        title: this.form.title || "",
        tagline: this.form.tagline || "",
        hashtag: this.form.hashtag || "",
        buttons: buttons,
        circleUrls: this.circleUrls,
      };
    },
  },
  watch: {
    hero: {
      immediate: true,
      handler: function () {
        this.hydrate();
      },
    },
  },
  methods: {
    emptyForm() {
      return {
        layout: "composed",
        banner_media_id: null,
        kicker: "",
        title: "",
        tagline: "",
        hashtag: "",
        button_1_label: "",
        button_1_url: "",
        button_2_label: "",
        button_2_url: "",
        circle_1_media_id: null,
        circle_2_media_id: null,
        circle_3_media_id: null,
        circle_4_media_id: null,
      };
    },
    hydrate() {
      var h = this.hero || {};
      this.form = {
        layout: h.layout || "composed",
        banner_media_id: h.banner_media_id || null,
        kicker: h.kicker || "",
        title: h.title || "",
        tagline: h.tagline || "",
        hashtag: h.hashtag || "",
        button_1_label: h.button_1_label || "",
        button_1_url: h.button_1_url || "",
        button_2_label: h.button_2_label || "",
        button_2_url: h.button_2_url || "",
        circle_1_media_id: h.circle_1_media_id || null,
        circle_2_media_id: h.circle_2_media_id || null,
        circle_3_media_id: h.circle_3_media_id || null,
        circle_4_media_id: h.circle_4_media_id || null,
      };
      this.cacheMedia("banner_media_id", h.bannerMedia);
      this.cacheMedia("circle_1_media_id", h.circle1Media);
      this.cacheMedia("circle_2_media_id", h.circle2Media);
      this.cacheMedia("circle_3_media_id", h.circle3Media);
      this.cacheMedia("circle_4_media_id", h.circle4Media);
    },
    cacheMedia(field, item) {
      if (item && item.id) {
        this.mediaCache = Object.assign({}, this.mediaCache, {
          [String(item.id)]: cloneApolloData(item),
        });
      }
    },
    urlFor(id) {
      if (!id) return "";
      var item = this.mediaCache[String(id)];
      return mediaPublicUrl(item);
    },
    circleUrl(key) {
      var map = {
        circle1: "circle_1_media_id",
        circle2: "circle_2_media_id",
        circle3: "circle_3_media_id",
        circle4: "circle_4_media_id",
      };
      return this.urlFor(this.form[map[key]]);
    },
    openPicker(target) {
      this.pickerTarget = target;
    },
    clearMedia(target) {
      if (target === "banner") this.form.banner_media_id = null;
      if (target === "circle1") this.form.circle_1_media_id = null;
      if (target === "circle2") this.form.circle_2_media_id = null;
      if (target === "circle3") this.form.circle_3_media_id = null;
      if (target === "circle4") this.form.circle_4_media_id = null;
    },
    onMediaSelected(item) {
      if (!item) return;
      this.mediaCache = Object.assign({}, this.mediaCache, {
        [String(item.id)]: cloneApolloData(item),
      });
      if (this.pickerTarget === "banner") this.form.banner_media_id = item.id;
      if (this.pickerTarget === "circle1") this.form.circle_1_media_id = item.id;
      if (this.pickerTarget === "circle2") this.form.circle_2_media_id = item.id;
      if (this.pickerTarget === "circle3") this.form.circle_3_media_id = item.id;
      if (this.pickerTarget === "circle4") this.form.circle_4_media_id = item.id;
      this.pickerTarget = null;
    },
    async save() {
      this.saving = true;
      try {
        var result = await this.$apollo.mutate({
          mutation: SAVE_MUTATION,
          variables: {
            career_fair_id: this.careerFairId,
            input: {
              layout: this.form.layout,
              banner_media_id: this.form.banner_media_id || null,
              kicker: this.form.kicker || null,
              title: this.form.title || null,
              tagline: this.form.tagline || null,
              hashtag: this.form.hashtag || null,
              button_1_label: this.form.button_1_label || null,
              button_1_url: this.form.button_1_url || null,
              button_2_label: this.form.button_2_label || null,
              button_2_url: this.form.button_2_url || null,
              circle_1_media_id: this.form.circle_1_media_id || null,
              circle_2_media_id: this.form.circle_2_media_id || null,
              circle_3_media_id: this.form.circle_3_media_id || null,
              circle_4_media_id: this.form.circle_4_media_id || null,
            },
          },
          errorPolicy: "all",
          fetchPolicy: "no-cache",
        });
        var payload = result && result.data && result.data.upsertCareerFairHero;
        if (!payload && result && result.errors && result.errors.length) {
          if (this.$swal) {
            this.$swal.fire({
              icon: "error",
              title: "Could not save",
              text: result.errors[0].message || "Please try again.",
            });
          }
          return;
        }
        if (payload && payload.error) {
          if (this.$swal) this.$swal.fire({ icon: "error", title: "Could not save", text: payload.message });
        } else if (payload && payload.data) {
          if (this.$swal) {
            this.$swal.fire({
              toast: true,
              position: "top-end",
              icon: "success",
              title: "Hero saved",
              showConfirmButton: false,
              timer: 1500,
              timerProgressBar: true,
            });
          }
          this.$emit("saved", payload.data);
        } else {
          if (this.$swal) this.$swal.fire({ icon: "error", title: "Could not save", text: "Please try again." });
        }
      } catch (e) {
        var recovered =
          e && e.data && e.data.upsertCareerFairHero
            ? e.data.upsertCareerFairHero
            : null;
        if (recovered && !recovered.error && recovered.data) {
          if (this.$swal) {
            this.$swal.fire({
              toast: true,
              position: "top-end",
              icon: "success",
              title: "Hero saved",
              showConfirmButton: false,
              timer: 1500,
              timerProgressBar: true,
            });
          }
          this.$emit("saved", recovered.data);
          return;
        }
        if (this.$swal) {
          this.$swal.fire({
            icon: "error",
            title: "Could not save",
            text: (e && e.message) || "Please try again.",
          });
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.cf-circle-wrap {
  position: relative;
  width: 100%;
  padding-top: 100%;
}

.cf-circle-btn {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0;
  border-radius: 50%;
  border: 2px dashed #d1d5db;
  background: #f8fafc;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cf-circle-btn:hover {
  border-color: #499DD4;
}

.cf-circle-btn--filled {
  border-style: solid;
  border-color: #e5e7eb;
  background: #e5e7eb;
}

.cf-circle-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.cf-circle-close {
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 2;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #394867;
  color: #fff;
  font-size: 16px;
  line-height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(33, 42, 62, 0.35);
}

.cf-circle-close:hover {
  background: #9B2C2C;
}
</style>
